import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { FileDown, FileText, AlertTriangle } from 'lucide-react';
import { API } from '../../App';
import { downloadFile, blobErrorDetail } from '../../lib/downloadFile';

const TXT_FILES = [
  { kind: 'kwsp', label: 'KWSP Borang A (.txt)', hint: 'Upload to KWSP i-Akaun Majikan', readiness: 'kwsp' },
  { kind: 'perkeso', label: 'PERKESO SOCSO + EIS (.txt)', hint: 'Upload to PERKESO ASSIST portal (v2.0)', readiness: 'perkeso' },
  { kind: 'lhdn', label: 'LHDN e-PCB CP39 (.txt)', hint: 'Upload to e-PCB Plus / e-Data PCB', readiness: 'lhdn' },
];
const PDF_FILES = [
  { kind: 'kwsp_borang_a', label: 'EPF Borang A (PDF)' },
  { kind: 'perkeso_8a', label: 'SOCSO Borang 8A (PDF)' },
];

export const StatutoryExportBar = ({ month, year, readiness = {}, kwspMonth, disabled }) => {
  const [busy, setBusy] = useState('');
  const period = `${year}${String(month).padStart(2, '0')}`;

  const run = async (key, url, fallbackName) => {
    setBusy(key);
    try {
      const name = await downloadFile(url, fallbackName);
      toast.success(`${name} downloaded`);
    } catch (err) {
      toast.error(await blobErrorDetail(err, 'Export failed'));
    } finally {
      setBusy('');
    }
  };

  const missingAny = Object.values(readiness).some((m) => m && m.length);

  return (
    <div className="space-y-3" data-testid="statutory-export-bar">
      <div className="flex flex-wrap gap-2">
        {TXT_FILES.map((f) => {
          const missing = readiness[f.readiness] || [];
          return (
            <button
              key={f.kind}
              type="button"
              data-testid={`export-txt-${f.kind}`}
              disabled={disabled || busy !== '' || missing.length > 0}
              title={missing.length ? `Missing in Company Settings: ${missing.join(', ')}` : f.hint}
              onClick={() => run(f.kind, `${API}/payroll/statutory-txt/${f.kind}?month=${month}&year=${year}`, `${f.kind.toUpperCase()}_${period}.txt`)}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-md bg-slate-900 text-white hover:bg-slate-700 disabled:opacity-40 disabled:cursor-not-allowed dark:bg-emerald-600 dark:hover:bg-emerald-500"
            >
              <FileDown className="h-3.5 w-3.5" />
              {busy === f.kind ? 'Preparing…' : f.label}
            </button>
          );
        })}
        {PDF_FILES.map((f) => (
          <button
            key={f.kind}
            type="button"
            data-testid={`export-pdf-${f.kind}`}
            disabled={disabled || busy !== ''}
            onClick={() => run(f.kind, `${API}/payroll/gov-forms/${f.kind}?month=${month}&year=${year}`, `${f.kind}_${period}.pdf`)}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-md border border-slate-300 text-slate-800 hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed dark:border-slate-600 dark:text-slate-100 dark:hover:bg-slate-700"
          >
            <FileText className="h-3.5 w-3.5" />
            {busy === f.kind ? 'Preparing…' : f.label}
          </button>
        ))}
      </div>
      <p className="text-xs text-slate-500 dark:text-slate-400" data-testid="statutory-month-note">
        KWSP file is for contribution month <span className="font-semibold">{kwspMonth || '—'}</span> (salary month + 1).
        PERKESO and LHDN files use the salary month {String(month).padStart(2, '0')}/{year}.
      </p>
      {missingAny && (
        <div className="flex items-start gap-2 text-xs rounded-md border border-amber-300 bg-amber-50 text-amber-900 p-2.5 dark:bg-amber-900/20 dark:border-amber-700 dark:text-amber-200" data-testid="statutory-readiness-warning">
          <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
          <span>
            Some TXT files are disabled until the employer registration numbers are filled in.{' '}
            <Link to="/dashboard/settings" className="font-semibold underline" data-testid="statutory-settings-link">Open Company Settings</Link>
          </span>
        </div>
      )}
    </div>
  );
};
